/**
 * The style block: how ONE panel looks, on top of whatever the page's theme says. Today it holds
 * one setting, `seriesColors`: the colour a named series or category paints on this panel
 * ("Gryffindor" red on this pie, "clicks" blue on this bar chart), as a light/dark pair like every
 * other Analytics colour. A name the panel is not drawing is inert; a series with no entry keeps
 * the colour the theme hands out by position.
 *
 * Keyed by the NAME, not the position, because the position is not stable: a pie orders its slices
 * largest-first, so "the first slice is red" moves to another category the moment the numbers
 * change, while "Gryffindor is red" does not. The name is whatever the renderer colours by: the
 * slice label, the series column key, the funnel step, the status, the state, the bubble category,
 * the flow node, the marker group.
 *
 * Map-shaped like `format` and `links`: one entry is set without resending the others, and null
 * takes one off. Carried by the types whose renderer hands out a colour per name; storing a colour
 * on a stat tile would be the silent no-op the carrier gate exists to prevent.
 */
import describeUnknownKey from '../describeUnknownKey.js';
import { ANALYTICS_COLOR_PAIR_TOOL_SCHEMA, toAnalyticsColorPairHex, validateAnalyticsColorPair } from '../analyticsColorPair.js';

const STYLE_KEYS = ['seriesColors'];

/* A name is a value read off the rows or a column key, so it is bounded the way a row-field
   pointer is. The entry cap keeps a runaway writer from storing a colour per row of a large table. */
const SERIES_NAME_MAX_CHARS = 200;
const SERIES_COLORS_MAX_ENTRIES = 64;

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/* Render-side reading of a stored block: tolerant, malformed entries dropped. Returns
   { seriesColors: { [name]: { light, dark } } } with only the usable pairs, or an empty object. */
export const normalizeAnalyticsPanelStyle = (block) => {
  if (!isPlainObject(block)) {
    return {};
  }

  const seriesColors = {};

  for (const [name, pair] of Object.entries(isPlainObject(block.seriesColors) ? block.seriesColors : {})) {
    const check = validateAnalyticsColorPair(pair, name);

    if (name && check.ok && check.value) {
      seriesColors[name] = check.value;
    }
  }

  return Object.keys(seriesColors).length > 0 ? { seriesColors } : {};
};

/* The colours a renderer paints by name for one scheme: { [name]: '#rrggbb' }. Null when the block
   names none, so a renderer can skip the lookup entirely. */
export const resolveAnalyticsPanelSeriesColors = ({ style, scheme = 'light' } = {}) => {
  const entries = Object.entries(normalizeAnalyticsPanelStyle(style).seriesColors || {})
    .map(([name, pair]) => [name, toAnalyticsColorPairHex(pair, scheme)])
    .filter(([, hex]) => hex);

  return entries.length > 0 ? Object.fromEntries(entries) : null;
};

/* Write-side check, shared by the tool and display code. `allowRemovals` accepts null entries and
   a null map, which is how the merging tool path says "take this colour off" / "back to the theme";
   display code returns the whole block fresh each resolution, so a removal token there is a typo. */
export const validateAnalyticsPanelStyle = (block, { allowRemovals = false } = {}) => {
  if (!isPlainObject(block)) {
    return 'style must be an object like { "seriesColors": { "Gryffindor": { "light": "#7f0909", "dark": "#c41e1e" } } }.';
  }

  const unknown = describeUnknownKey({ value: block, allowed: STYLE_KEYS, where: 'style' });

  if (unknown) {
    return unknown;
  }

  if (block.seriesColors === undefined) {
    return '';
  }

  if (allowRemovals && block.seriesColors === null) {
    return '';
  }

  if (!isPlainObject(block.seriesColors)) {
    return 'style.seriesColors must be an object keyed by the series or category name, each a {light, dark} hex pair.';
  }

  const names = Object.keys(block.seriesColors);

  if (names.length > SERIES_COLORS_MAX_ENTRIES) {
    return `style.seriesColors names at most ${SERIES_COLORS_MAX_ENTRIES} series or categories.`;
  }

  for (const name of names) {
    if (!name.trim() || name.length > SERIES_NAME_MAX_CHARS) {
      return 'Every style.seriesColors key must be the non-empty name of a series or category the panel draws.';
    }

    const pair = block.seriesColors[name];

    if (allowRemovals && pair === null) {
      continue;
    }

    const check = validateAnalyticsColorPair(pair, `style.seriesColors["${name}"]`);

    if (!check.ok) {
      return check.message;
    }

    if (!check.value) {
      return `style.seriesColors["${name}"] must be a {light, dark} hex pair.`;
    }
  }

  return '';
};

/* The merge, hand-written because the block is a map (see format.js): a caller colours ONE name
   without resending the others, null takes a name's colour off, and `seriesColors: null` takes them
   all off. Assumes the requested block already passed validateAnalyticsPanelStyle with removals
   allowed. Returns null when nothing is left, which every other cleared block also stores. */
export const mergeAnalyticsPanelStyle = ({ requested, stored } = {}) => {
  const merged = { ...(normalizeAnalyticsPanelStyle(stored).seriesColors || {}) };

  if (isPlainObject(requested) && requested.seriesColors === null) {
    return null;
  }

  for (const [name, pair] of Object.entries(isPlainObject(requested?.seriesColors) ? requested.seriesColors : {})) {
    if (pair === null) {
      delete merged[name];
      continue;
    }

    const check = validateAnalyticsColorPair(pair, name);

    if (check.ok && check.value) {
      merged[name] = check.value;
    }
  }

  return Object.keys(merged).length > 0 ? { seriesColors: merged } : null;
};

const style = {
  /* No names: a block, not a renderer. */
  names: [],
  block: 'style',
  /* Not one type's settings, so the tool declares it by hand beside `format` and `links`, and the
     per-type loops skip it. Gated to the renderers that hand out a colour per series or category:
     a stat tile, a table or a heatmap has no series to colour. A type joins by one id here plus its
     renderer reading the resolved colours. */
  sharedBlock: true,
  carriedBy: ['bar', 'line', 'combo', 'pareto', 'pie', 'treemap', 'funnel', 'bump', 'bubble', 'quadrant', 'statusgrid', 'sankey', 'chord', 'statetimeline', 'map'],
  /* No `fields`: `seriesColors` is a map keyed by names read off the panel's own rows, which the
     field-spec vocabulary cannot describe. Merge and validation are hand-written above. */
  toolSchema: {
    type: ['object', 'null'],
    additionalProperties: false,
    description: 'How THIS panel looks on top of the page theme. Only for a colour the user pins to a specific series or category on this one chart ("make Gryffindor red", "clicks in blue"); a look for the whole page or every page is a theme or page style, never this.',
    properties: {
      seriesColors: {
        type: ['object', 'null'],
        additionalProperties: ANALYTICS_COLOR_PAIR_TOOL_SCHEMA,
        description: 'A light/dark pair per NAME the panel colours by, exactly as the value appears in the rows: the slice label (pie, treemap), the series column key (bar, line, combo), the step (funnel), the status or state, the bubble category, the flow node, the marker group. {"Gryffindor": {"light": "#7f0909", "dark": "#c41e1e"}}. Merged over what is stored, so colour one name without resending the others; a name set to null goes back to the theme colour; the whole map set to null clears every pinned colour. A name the panel is not drawing is inert.'
      }
    }
  }
};

export default style;
